/**
 * ClientInvitePage — WL-K5a Kanzlei-Sicht: „Mandanten einladen".
 *
 * Konto-Seite unter `/dashboard/konto/mandanten-einladen`, über die eine Kanzlei
 * (StB) ihre Mandanten per E-Mail zu FaeBill einlädt (Reverse-Invite). Ablauf:
 *  1. Loop-B-Beta-Gate (kosmetisch; der Server erzwingt es fail-closed) — ein
 *     Nicht-Beta-Nutzer sieht die Seite nicht (nur einen neutralen Hinweis).
 *  2. Kanzlei-Profil + AVV: fehlt der `advisor_profiles`-Eintrag oder die AVV-
 *     Zustimmung, zeigt die Seite zuerst das Profil-/AVV-Formular
 *     ({@link upsertMyAdvisorProfile}); der Server verlangt `avvAcceptedAt` vor
 *     jeder Einladung (K5a-1).
 *  3. Danach: Einzel-Einladungsformular ({@link ClientInvitePanel}) + gesendete
 *     Einladungen ({@link SentClientInvitesPanel}).
 *
 * @module ClientInvitePage
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClients } from '../icons';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import { canUseClientInvite } from '../config/kanzleiAccess';
import logger from '../utils/logger';
import { upsertMyAdvisorProfile } from '../api/clientInviteApi';
import ClientInvitePanel from '../components/tenant/ClientInvitePanel';
import SentClientInvitesPanel from '../components/tenant/SentClientInvitesPanel';
import BulkInvitePanel from '../components/tenant/BulkInvitePanel';
import SimpleMarkdown from '../components/inbound/SimpleMarkdown';
import {
  KANZLEI_ONBOARDING_DOC_PATH,
  CLIENT_INVITE_AVV_VERSION,
  LEGAL_DRAFT_MARKER,
} from '../config/advisorConsentAccess';
import {
  useLegalDocument,
  interpolatePlaceholder,
} from '../components/tenant/useLegalDocument';

const ClientInvitePage: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, sellerData } = useAuth();

  const isBeta = canUseClientInvite(currentUser?.uid, sellerData);

  const [profileLoading, setProfileLoading] = useState(true);
  const [avvAccepted, setAvvAccepted] = useState(false);
  const [kanzleiName, setKanzleiName] = useState('');
  const [beraterNr, setBeraterNr] = useState('');
  const [avvChecked, setAvvChecked] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  // §5-Kanzlei-Onboarding (B4): ist die §5-Datei freigegeben (`ready`), ersetzt ihr
  // Wortlaut den bisherigen Kurz-AVV-Text und der eine Haken bestätigt §5 (=Superset
  // des AVV). Fehlt/Draft ⇒ Bestandsverhalten (Kurz-AVV, kein onboardingConfirmed).
  const onboardingDoc = useLegalDocument(KANZLEI_ONBOARDING_DOC_PATH, LEGAL_DRAFT_MARKER);
  const onboardingReady = onboardingDoc.kind === 'ready';

  const loadProfile = useCallback(async () => {
    if (!currentUser?.uid || !isBeta) {
      setProfileLoading(false);
      return;
    }
    setProfileLoading(true);
    try {
      const snap = await getDoc(doc(db, 'advisor_profiles', currentUser.uid));
      const data = snap.exists() ? snap.data() : null;
      setAvvAccepted(!!data?.avvAcceptedAt);
      if (typeof data?.kanzleiName === 'string') setKanzleiName(data.kanzleiName);
      if (typeof data?.beraterNr === 'string') setBeraterNr(data.beraterNr);
    } catch (err) {
      logger.error('[ClientInvitePage] profile load failed', err);
      // Fail-safe: treat as not-accepted so the AVV form is shown.
      setAvvAccepted(false);
    } finally {
      setProfileLoading(false);
    }
  }, [currentUser?.uid, isBeta]);

  useEffect(() => { void loadProfile(); }, [loadProfile]);

  const handleSaveProfile = async () => {
    setProfileError(null);
    if (!kanzleiName.trim()) {
      setProfileError(t('clientInvite.profile.nameRequired'));
      return;
    }
    if (!avvChecked) {
      setProfileError(t('clientInvite.profile.avvRequired'));
      return;
    }
    setSavingProfile(true);
    try {
      // §5-Pfad: EIN Haken bestätigt AVV UND §5-Onboarding. Inerter Kurz-AVV-Pfad:
      // nur avvAccepted (kein onboardingConfirmed).
      await upsertMyAdvisorProfile(
        kanzleiName.trim(),
        beraterNr.trim() || null,
        true,
        onboardingReady ? true : undefined,
      );
      setAvvAccepted(true);
    } catch (err) {
      logger.error('[ClientInvitePage] profile save failed', err);
      setProfileError(t('clientInvite.profile.saveError'));
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-primary flex items-center gap-3">
            <IconClients size={20} className="text-accent-ink" />
            {t('clientInvite.pageTitle')}
          </h1>
          <p className="text-muted text-sm mt-1">{t('clientInvite.pageSubtitle')}</p>
        </div>

        {!isBeta ? (
          <p className="rounded-lg bg-surface-raised p-4 text-sm text-muted">
            {t('clientInvite.notAvailable')}
          </p>
        ) : profileLoading ? (
          <p className="rounded-lg bg-surface-raised p-4 text-sm text-faint">{t('clientInvite.loading')}</p>
        ) : !avvAccepted ? (
          <section className="rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5 p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-3">
              {t('clientInvite.profile.title')}
            </h2>
            <label htmlFor="kanzlei-name-input" className="block text-xs font-medium text-muted mb-1">
              {t('clientInvite.profile.nameLabel')}
            </label>
            <input
              id="kanzlei-name-input"
              type="text"
              value={kanzleiName}
              onChange={(e) => setKanzleiName(e.target.value)}
              placeholder={t('clientInvite.profile.namePlaceholder')}
              disabled={savingProfile}
              className="w-full rounded-md bg-surface border border-hairline px-2 py-1.5 text-sm text-primary placeholder:text-faint focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
            />

            <label htmlFor="berater-nr-input" className="block text-xs font-medium text-muted mb-1 mt-3">
              {t('clientInvite.profile.beraterNrLabel')}
            </label>
            <input
              id="berater-nr-input"
              type="text"
              inputMode="numeric"
              value={beraterNr}
              onChange={(e) => setBeraterNr(e.target.value)}
              placeholder={t('clientInvite.profile.beraterNrPlaceholder')}
              disabled={savingProfile}
              className="w-full rounded-md bg-surface border border-hairline px-2 py-1.5 text-sm text-primary placeholder:text-faint focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
            />

            {/* §5-Onboarding-Dokument (B4) statt Kurz-AVV, sobald die §5-Datei
                freigegeben ist. Fehlt/Draft ⇒ inerter Kurz-AVV-Text (Bestand). */}
            {onboardingReady && (
              <div
                className="mt-3 max-h-56 overflow-y-auto rounded-md border border-hairline bg-surface p-3 text-xs text-secondary"
                data-testid="client-invite-onboarding-doc"
              >
                <SimpleMarkdown
                  text={interpolatePlaceholder(
                    onboardingDoc.kind === 'ready' ? onboardingDoc.text : '',
                    'avv_version',
                    CLIENT_INVITE_AVV_VERSION,
                    CLIENT_INVITE_AVV_VERSION,
                  )}
                />
              </div>
            )}

            <label className="mt-3 flex items-start gap-2 text-[12px] leading-relaxed text-secondary">
              <input
                type="checkbox"
                checked={avvChecked}
                onChange={(e) => setAvvChecked(e.target.checked)}
                disabled={savingProfile}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500"
              />
              <span>
                {onboardingReady
                  ? t('kanzleiOnboarding.checkboxLabel')
                  : t('clientInvite.profile.avvLabel')}
              </span>
            </label>

            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={savingProfile || !kanzleiName.trim() || !avvChecked}
              className="mt-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingProfile ? t('clientInvite.working') : t('clientInvite.profile.saveButton')}
            </button>

            {profileError && <p className="mt-2 text-xs text-[var(--tone-danger-fg)]">{profileError}</p>}
          </section>
        ) : (
          <section className="rounded-lg bg-surface-raised ring-1 ring-black ring-opacity-5 p-4">
            <div className="mb-4 flex gap-1 rounded-md bg-surface p-1 text-sm">
              <button
                type="button"
                onClick={() => setMode('single')}
                className={`flex-1 rounded px-3 py-1.5 font-medium transition-colors ${
                  mode === 'single' ? 'bg-indigo-600 text-white' : 'text-muted hover:text-primary'
                }`}
              >
                {t('clientInvite.tabSingle')}
              </button>
              <button
                type="button"
                onClick={() => setMode('bulk')}
                className={`flex-1 rounded px-3 py-1.5 font-medium transition-colors ${
                  mode === 'bulk' ? 'bg-indigo-600 text-white' : 'text-muted hover:text-primary'
                }`}
              >
                {t('clientInvite.tabBulk')}
              </button>
            </div>

            {mode === 'single' ? (
              <>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-2">
                  {t('clientInvite.inviteSection')}
                </h2>
                <ClientInvitePanel onInviteSent={() => setRefreshKey((k) => k + 1)} />
                <SentClientInvitesPanel refreshKey={refreshKey} />
              </>
            ) : (
              <>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted mb-2">
                  {t('clientInvite.bulk.sectionTitle')}
                </h2>
                <BulkInvitePanel />
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default ClientInvitePage;
